import React from 'react';
import { Chip, CircularProgress } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { computeResourceDiscoveryApiRef } from '../../api';
import {
  CRDProjectCodeConfig,
  KubernetesResourceDetails,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import { Entity } from '@backstage/catalog-model';
import { COMPUTE_RESOURCES_METADATA } from '../useProjectCode';

interface KubernetesStatusCellProps {
  entity: Entity;
}

const KubernetesStatusCell: React.FC<KubernetesStatusCellProps> = ({
  entity,
}) => {
  const [status, setStatus] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const api = useApi(computeResourceDiscoveryApiRef);

  React.useEffect(() => {
    const fetchKubernetesStatus = async () => {
      try {
        // Get kubernetes config from entity metadata.computeResources
        const metadata = entity.metadata as Record<string, any> | undefined;
        if (!metadata) {
          setStatus(null);
          setLoading(false);
          return;
        }
        const computeResources = metadata[COMPUTE_RESOURCES_METADATA] as
          | CRDProjectCodeConfig
          | undefined;
        if (!computeResources) {
          setStatus(null);
          setLoading(false);
          return;
        }
        const projectCode = computeResources.project_code;
        const kubernetesConfigs = computeResources.kubernetes;

        if (
          !projectCode ||
          !kubernetesConfigs ||
          kubernetesConfigs.length === 0
        ) {
          setStatus(null);
          setLoading(false);
          return;
        }

        // Fetch resources from all kubernetes contexts
        const allResourcesPromises = kubernetesConfigs.map(config =>
          api
            .listResources({
              serviceType: 'kubernetes',
              resourceType: 'resources',
              projectCode: projectCode,
              entityRef: `${entity.kind.toLowerCase()}:${
                entity.metadata.namespace
              }/${entity.metadata.name}`,
              cloudConfig: { kubeContext: config.kubeContext },
            })
            .catch(error => {
              console.error(
                `Failed to fetch resources from ${config.kubeContext}:`,
                error,
              );
              return [];
            }),
        );

        const allResourcesArrays = await Promise.all(allResourcesPromises);
        const allResources = allResourcesArrays.flat();

        if (!allResources || allResources.length === 0) {
          setStatus('No Resources');
          setLoading(false);
          return;
        }

        // Calculate status based on all resources from all contexts
        const kubernetesResources = allResources as KubernetesResourceDetails[];
        let totalReplicas = 0;
        let totalReady = 0;

        kubernetesResources.forEach((resource: KubernetesResourceDetails) => {
          if (resource.replicas !== undefined) {
            totalReplicas += resource.replicas;
            totalReady += resource.ready || 0;
          }
        });

        if (totalReplicas === 0) {
          setStatus('All Stopped');
        } else if (totalReady === 0) {
          setStatus('All Stopped');
        } else if (totalReady === totalReplicas) {
          setStatus('All Running');
        } else {
          setStatus('Partially Running');
        }
      } catch (error) {
        setStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchKubernetesStatus();
  }, [entity, api]);

  if (loading) {
    return <CircularProgress size={20} />;
  }

  const displayStatus = status || 'No Resources';

  // Color mapping for different statuses
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'All Running':
        return '#4caf50'; // green
      case 'Partially Running':
        return '#ff9800'; // orange
      case 'All Stopped':
        return '#f44336'; // red
      default:
        return '#9e9e9e'; // grey
    }
  };

  return (
    <Chip
      label={displayStatus}
      size="small"
      style={{
        backgroundColor: getStatusColor(displayStatus),
        color: 'white',
        fontWeight: 500,
      }}
    />
  );
};

/**
 * Creates a catalog table status column that shows Kubernetes resource status
 * Status is calculated from all Kubernetes resources (deployments, statefulsets, etc.)
 * - All Running: All replicas are ready
 * - Partially Running: Some replicas are ready
 * - All Stopped: No replicas are ready
 */
export const createKubernetesStatusColumn =
  (): TableColumn<CatalogTableRow> => {
    return {
      title: 'Status',
      field: 'entity.metadata.name',
      render: ({ entity }) => <KubernetesStatusCell entity={entity} />,
      width: '10%',
    };
  };
